import { i18n } from "@lingui/core";
import { Group, Select } from "@mantine/core";
import {
	useFetcher,
	useFetchers,
	useMatches,
	useRouteLoaderData,
} from "@remix-run/react";

export function useLocale(localeKey = "locale"): string {
	const [rootMatch] = useMatches();
	const { [localeKey]: locale } =
		(rootMatch.data as Record<string, string>) ?? {};
	if (typeof locale === "string") return locale;
	return "id"; // fallback to id
}

export function useOptimisticLocale() {
	const fetchers = useFetchers();
	const themeFetcher = fetchers.find((f) => f.formAction === "/");

	if (themeFetcher?.formData) {
		const submission = Object.fromEntries(themeFetcher.formData);

		// Use Valibot or zod to validate
		if (
			submission.status === "success" &&
			typeof submission.value === "object" &&
			"locale" in submission.value
		) {
			return submission.value.locale as string;
		}
	}
}

export function getLanguages() {
	return [
		{
			value: "id",
			label: "Indonesia",
		},
		{
			value: "en",
			label: "English",
		},
		{
			value: "jp",
			label: "Jepang",
		},
	];
}

function renderOption({
	option,
}: { option: { label: string; value: string } }) {
	return (
		<Group flex="1" gap="xs">
			<img className="w-6" src={`/icons/flag/${option.value}.svg`} />
			{option.label}
		</Group>
	);
}

export function LocaleSelector() {
	const languages = getLanguages();
	const { locale, setLocale } = useLocaleSelector();

	return (
		<Select
			renderOption={renderOption}
			checkIconPosition="left"
			value={locale}
			onChange={(v) => v && setLocale(v)}
			data={languages}
			name="locale"
		/>
	);
}

export function useLocaleSelector() {
	const data = useRouteLoaderData<{ locale: string }>("root");
	const fetcher = useFetcher();

	const optimisticLocale = useOptimisticLocale();
	const locale = optimisticLocale ?? data?.locale ?? "id";

	const setLocale = (locale: string) => {
		fetcher.submit(
			{
				locale,
			},
			{
				method: "POST",
				action: "/",
			},
		);
	};

	return {
		locale,
		setLocale,
	};
}
